<template>
  <DetailsSection
    label="Tarif"
    :class="isPremium ? 'border-bottom-0' : ''"
  >
    <div v-if="priceModelOptions.length > 6">
      <ToggleGroup
        :options="priceModelOptionsBasic"
        :select="setPriceModel"
        :selected-key="priceModel"
      />
      <ToggleGroup
        class="mt-1"
        :options="priceModelOptionsSpecials"
        :select="setPriceModel"
        :selected-key="priceModel"
      />
    </div>
    <ToggleGroup
      v-else
      :options="priceModelOptions"
      :select="setPriceModel"
      :selected-key="priceModel"
    />
  </DetailsSection>

  <DetailsSection
    v-if="isPremium"
    label="Kabinentyp"
    class="border-top-0"
  >
    <ToggleGroup
      :options="typeOptions"
      :select="setType"
      :selected-key="type"
    />
  </DetailsSection>

  <DetailsSection
    v-if="isPremium"
    label="Wunschkabine"
    class="mt-2"
  >
    <div class="row">
      <div class="col">
        <TextField
          placeholder=""
          field-name="userChoice"
          :field-value="userChoice"
          :value-changed="userChoiceChanged"
          :enter="checkUserChoice"
        />
      </div>
      <div class="col">
        <ExpiButton
          button-style="outline"
          label-id="Verfügbarkeit prüfen"
          :click="checkUserChoice"
        />
      </div>
    </div>
    <div class="d-flex align-items-center m-1">
      <span class="text fw-bold" />
      <span class="info">
        <i class="fa fa-info-circle color-expinet-blue" />
        {{ userChoiceMessage }}
      </span>
    </div>
  </DetailsSection>

  <DetailsSection
    label="Verfügbare Kabinenkategorien"
    type="last"
    class="mt-2"
  >
    <div class="category-selector">
      <div class="w-100 d-flex flex-column align-items-stretch">
        <radio-buttons
          :items="matchingCategories" 
          :selected-item="selectedCategory?.id"
          :select="selectCategory" 
          :displayed-label="checkboxLabel"
          :use-combo-button="true"
          :clicked-forward="clickNext"
        />
      </div>
    </div>
  </DetailsSection>
</template>

<script lang="ts">
import ExpiButton from '@/components/common/ExpiButton.vue'
import RadioButtons from '@/components/common/RadioButtons.vue'
import { useCabinStore, useDetailsStore, usePriceStore } from '@/store'
import { CabinCategory } from '@/store/details/cabin/interfaces'
import { mapPriceModelLabel } from '@/store/details/helper'
import { CabinCategoryID, IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { COMFORTAI, IND, INDAI } from '../../../general/master-data'
import TextField from '../../common/TextField.vue'
import ToggleGroup from '../../common/ToggleGroup.vue'
import DetailsSection from '../DetailsSection.vue'

export default defineComponent({
  components: {
    DetailsSection,
    TextField,
    ToggleGroup,
    RadioButtons,
    ExpiButton
  },

  props: {
    clickNext: { type: Function, default: () => console.log('clickNext is not implemented') }, // eslint-disable-line no-console
  },

  computed: {
    ...mapState(useDetailsStore, {
      priceModel: store => store.priceModel,
      priceModels: store => store.priceModels
    }),
    ...mapState(useCabinStore, {
      selectedCategory: store => store.category,
      userChoiceMessage: store => store.userChoiceMessage,
      cabinTypes: store => store.cabinTypes,
      matchingCategories: store => store.matchingCategories,
    }),
    ...mapWritableState(useCabinStore, {
      type: 'type',
    }),
    ...mapWritableState(useCabinStore, {
      userChoice: 'userChoice',
    }),
    ...mapState(usePriceStore, {
      displayCategoryPrice: store => store.category,
    }),
    priceModelOptions() {
      return this.priceModels.map(pm => {
        return {
          key: pm,
          label: mapPriceModelLabel(pm),
          available: true,
        }
      })
    },
    priceModelOptionsBasic() {
      return this.priceModelOptions.filter(option => !['LIG', 'PAU', 'PAUAI'].includes(option.key))
    },
    priceModelOptionsSpecials() {
      return this.priceModelOptions.filter(option => ['LIG', 'PAU', 'PAUAI'].includes(option.key))
    },
    typeOptions() {
      return this.cabinTypes.map(type => {
        return {
          key: type,
          label: type,
          available: true,
        }
      })
    },
    isPremium() {
      return [COMFORTAI, IND, INDAI].includes(this.priceModel!)
    }
  },

  methods: {
    ...mapActions(useCabinStore, {
      selectType: 'selectType',
      setCategory: 'selectCategory',
      validateUserChoice: 'checkUserChoice'
    }),
    ...mapActions(useDetailsStore, {
      selectPriceModel: 'selectPriceModel'
    }),
    setType(type: string) {
      this.selectType(type)
    },
    setPriceModel(priceModel: IbePriceModelCode) {
      if (this.priceModel === priceModel) return

      this.selectPriceModel(priceModel)
    },
    selectCategory(categoryId: CabinCategoryID) {
      this.setCategory(categoryId)
      this.type = this.selectedCategory?.type ?? ''
    },
    userChoiceChanged(_: string, cabinId: string) {
      this.userChoice = cabinId

      if (!cabinId) {
        this.userChoiceMessage = 'Bitte Kabinennummer eingeben.'
      }
    },
    async checkUserChoice() {
      if (!this.userChoice) {
        this.userChoiceMessage = 'Bitte Kabinennummer eingeben.'
        return
      }
      await this.validateUserChoice()
    },

    checkboxLabel: (category: CabinCategory) => {
      return category.head + ' (' + category.id + ')'
    },

    getTranslateText: (label: string) => {
      return label
    }
  },
})
</script>

<style lang="scss" scoped>
@import '../../../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

.category-selector {
  overflow-y: scroll;
}
</style>
